import React from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeedbackCollector, { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getAttribution } from '../../../common/analytics/attribution';
import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { SentryErrorBoundary } from '../../../common/sentry';

import { messages } from './messages';

const styles = cssMap({
	floatingButton: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.200'),
		zIndex: 100,
	},
});

type CollectFeedbackProps = {
	atlassianAccountId: string;
	entrypointId: string;
	name: string | undefined;
};

export const CollectFeedback = ({
	atlassianAccountId,
	entrypointId,
	name,
}: CollectFeedbackProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [displayFlag, setDisplayFlag] = React.useState(false);
	const { fireEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleOpen = React.useCallback(() => {
		fireEvent('ui.feedbackButton.clicked.twgMicrositeHome', {
			...getAttribution(),
		});
		setIsOpen(true);
	}, [fireEvent]);
	const handleClose = React.useCallback(() => setIsOpen(false), []);
	const handleSubmit = React.useCallback(() => setDisplayFlag(true), []);
	const hideFlag = React.useCallback(() => setDisplayFlag(false), []);

	return (
		<SentryErrorBoundary fallback={null} touchpointId="collect-feedback-v1">
			<Box xcss={styles.floatingButton}>
				<IconButton
					icon={MegaphoneIcon}
					label={formatMessage(messages.giveFeedbackLabel)}
					shape="circle"
					onClick={handleOpen}
					appearance="discovery"
				/>
			</Box>

			{isOpen && (
				<FeedbackCollector
					onClose={handleClose}
					onSubmit={handleSubmit}
					atlassianAccountId={atlassianAccountId}
					name={name}
					entrypointId={entrypointId}
				/>
			)}

			<FlagGroup onDismissed={hideFlag}>{displayFlag && <FeedbackFlag />}</FlagGroup>
		</SentryErrorBoundary>
	);
};
